import type { App } from '../app/App';
import { t } from '../i18n';
import { modal } from './components';
import { h } from './dom';

/**
 * Drawing rules: what may not be drawn. Shown from the creator, and accepted
 * once (per RULES_VERSION) before a drawing first leaves this device: share
 * codes, the contest, dares, online battles and Facebook pictures.
 */

const RULES_VERSION = '2026-10-21';
const KEY = 'paper-legends/drawing-rules';

export const DRAWING_RULES: string[] = [
  'No nudity, private body parts or anything sexual.',
  'No blood, gore or scenes meant to be truly scary or cruel.',
  'No hate symbols, or drawings that make fun of a religion, race, country or anyone for who they are.',
  'No drugs, cigarettes, alcohol or gambling.',
  'No rude words, swearing or rude gestures written or drawn into the picture.',
  'No personal details: names, phone numbers, addresses, school names or social media accounts.',
  'No drawings that tease or bully a real person.',
  "No logos or characters copied from someone else's work.",
];

function accepted(): boolean {
  try {
    return localStorage.getItem(KEY) === RULES_VERSION;
  } catch {
    return false;
  }
}

function rulesList(): HTMLElement {
  return h('ol', { class: 'drawing-rules-list' }, DRAWING_RULES.map((r) => h('li', null, t(r))));
}

const penalty = (): HTMLElement =>
  h('p', { class: 'hand-hint' }, t('Drawings that break these rules are removed. Breaking them again can pause or close your account.'));

/** Read-only rules (the creator's "Drawing rules" button). */
export async function showDrawingRules(): Promise<void> {
  await modal(t('Drawing rules'), h('div', { class: 'drawing-rules' }, h('p', null, t('Keep the notebook fun for everyone. Never draw:')), rulesList(), penalty()), [
    { label: t('Got it'), value: 'ok', variant: 'primary', color: 'yellow' },
  ]);
}

/** Before a drawing is shown to other people: true once the rules are accepted. */
export async function ensureDrawingRules(app: App): Promise<boolean> {
  if (accepted()) return true;
  const input = h('input', { type: 'checkbox', class: 'consent-check' }) as HTMLInputElement;
  const hint = h('p', { class: 'consent-hint', 'aria-live': 'polite' });
  const body = h(
    'div',
    { class: 'drawing-rules' },
    h('p', null, t('Other players will see this drawing. It must follow the drawing rules. Never draw:')),
    rulesList(),
    penalty(),
    h('label', { class: 'consent-row' }, input, h('span', null, t('My drawing follows the drawing rules.'))),
    hint,
  );
  const v = await modal(
    t('Drawing rules'),
    body,
    [
      { label: t('Cancel'), value: 'no', variant: 'ghost' },
      { label: t('I agree'), value: 'ok', variant: 'primary', color: 'green' },
    ],
    {
      accept: (value) => {
        if (value !== 'ok' || input.checked) return true;
        hint.textContent = t('Tick the box to agree to the drawing rules.');
        return false;
      },
    },
  );
  if (v !== 'ok') return false;
  try {
    localStorage.setItem(KEY, RULES_VERSION);
  } catch {
    /* asked again next time */
  }
  app.sfx.play('pop');
  return true;
}
